import { useEffect, useContext } from "react";
import {
	BrowserRouter as Router,
	Routes,
	Route,
	Navigate,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

// Pages
import Home from "./pages/Home";
import History from "./pages/History";
import Analysis from "./pages/Analysis";
import Add from "./pages/Add";
import Settings from "./pages/Settings";
import Login from "./pages/Login";
import Register from "./pages/Register";
import AuthPage from "./pages/Auth";
import NotFound from "./pages/NotFound";
import Admin from "./pages/Admin";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Terms from "./pages/Terms";
import RecurringTransactions from "./pages/RecurringTransactions";

// Components
import BottomNav from "./components/BottomNav/BottomNav";
import Loader from "./components/Loader/Loader";
import AuthRoutes from "./components/AuthRoutes/AuthRoutes";
import PageTitleManager from "./components/PageTitleManager/PageTitleManager";

// Hooks and context
import { useTheme } from "./hooks/useTheme";
import { useDesign } from "./hooks/useDesign";
import { I18nProvider, detectPreferredLocale } from "./i18n/i18n";
import { UserPreferencesContext } from "./contexts/UserPreferencesContext";

import "./styles/main.css";

// Configuration React Query
const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 5 * 60 * 1000, // 5 minutes
			cacheTime: 30 * 60 * 1000, // 30 minutes
			retry: 3,
			retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
			refetchOnWindowFocus: false,
		},
	},
});

function App() {
	useEffect(() => {
		// iOS Safari: Ensures that :active works correctly
		document.addEventListener("touchstart", () => {}, { passive: true });
	}, []);

	const { locale, authInitialized, setTheme, setLocale, currentUser } =
		useContext(UserPreferencesContext);

	// Check for a new build ID and reload the app if it has changed
	const checkForNewBuild = async () => {
		try {
			const currentBuild = localStorage.getItem("buildId");
			const res = await fetch("/buildId.json", { cache: "no-cache" });
			const data = await res.json();

			if (!currentBuild) {
				localStorage.setItem("buildId", data.id);
				return;
			}

			if (data.id !== currentBuild) {
				localStorage.setItem("buildId", data.id);
				// Force a full reload to clear the cache and load the new build
				window.location.reload(true);
			}
		} catch (error) {
			console.warn("Build check failed", error);
		}
	};

	// Run the build check only once when the app loads
	useEffect(() => {
		checkForNewBuild();
	}, []);

	// This hook applies the saved theme on initial load
	useTheme();

	// This hook applies the saved design on initial load
	useDesign();

	// Add class if app is running in standalone PWA mode
	useEffect(() => {
		if (window.matchMedia("(display-mode: standalone)").matches) {
			document.body.classList.add("standalone");
		}
	}, []);

	// Show the loader while authentication state is being initialized
	if (!authInitialized) {
		// Use detected locale for the loader when no user preferences are available yet
		const loaderLocale = locale || detectPreferredLocale();

		return (
			<I18nProvider locale={loaderLocale}>
				<div className="app-container">
					<Loader locale={loaderLocale} />
				</div>
			</I18nProvider>
		);
	}

	return (
		<QueryClientProvider client={queryClient}>
			<I18nProvider locale={locale}>
				<Router basename="/">
					<div className="app-container">
						{/* Automatically update the page title based on route */}
						<PageTitleManager />

						{/* Define application routes */}
						<Routes>
							<Route path="/" element={<Navigate to="/home" replace />} />

							<Route
								path="/home"
								element={
									<AuthRoutes requireAuth>
										<Home />
									</AuthRoutes>
								}
							/>

							<Route
								path="/transactions-history"
								element={
									<AuthRoutes requireAuth>
										<History />
									</AuthRoutes>
								}
							/>

							<Route
								path="/analysis"
								element={
									<AuthRoutes requireAuth>
										<Analysis />
									</AuthRoutes>
								}
							/>

							<Route
								path="/add-transaction"
								element={
									<AuthRoutes requireAuth>
										<Add />
									</AuthRoutes>
								}
							/>

							<Route
								path="/recurring-transactions"
								element={
									<AuthRoutes requireAuth>
										<RecurringTransactions />
									</AuthRoutes>
								}
							/>

							<Route
								path="/settings"
								element={
									<AuthRoutes requireAuth>
										<Settings setTheme={setTheme} setLocale={setLocale} />
									</AuthRoutes>
								}
							/>

							<Route
								path="/admin"
								element={
									<AuthRoutes requireAuth>
										<Admin setTheme={setTheme} setLocale={setLocale} />
									</AuthRoutes>
								}
							/>

							<Route
								path="/login"
								element={
									<AuthRoutes requireAuth={false}>
										<Login />
									</AuthRoutes>
								}
							/>

							<Route
								path="/register"
								element={
									<AuthRoutes requireAuth={false}>
										<Register />
									</AuthRoutes>
								}
							/>

							<Route
								path="/auth"
								element={
									<AuthRoutes requireAuth={false}>
										<AuthPage />
									</AuthRoutes>
								}
							/>

							<Route
								path="/privacy-policy"
								element={
									<AuthRoutes requireAuth={false}>
										<PrivacyPolicy />
									</AuthRoutes>
								}
							/>

							<Route
								path="/terms"
								element={
									<AuthRoutes requireAuth={false}>
										<Terms />
									</AuthRoutes>
								}
							/>

							{/* Catch-all route for 404 pages */}
							<Route path="*" element={<NotFound />} />
						</Routes>

						{/* Show bottom navigation only when user is authenticated */}
						{currentUser && <BottomNav />}

						{/* Toast notifications container */}
						<ToastContainer />
					</div>
				</Router>
			</I18nProvider>

			{/* DevTools React Query (dev only) */}
			{process.env.NODE_ENV === "development" && (
				<ReactQueryDevtools initialIsOpen={false} />
			)}
		</QueryClientProvider>
	);
}

export default App;
